import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Pagination,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { api } from '../../api';
import { useListUrlState } from '../../hooks/useListUrlState';

const logoColor = '#00798E';
const INDIA_COUNTRY_ID = 100;

const DOC_TYPES = [
  { label: 'Aadhaar', key: 'aadhaar', showIssuedBy: false, showValidTill: false },
  { label: 'PAN', key: 'pan', showIssuedBy: false, showValidTill: false },
  { label: 'Voter Card', key: 'voter', showIssuedBy: false, showValidTill: false },
  { label: 'Passport', key: 'passport', showIssuedBy: true, showValidTill: true },
  { label: 'Driving License', key: 'driving-liciense', showIssuedBy: true, showValidTill: true },
];

const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const defaultSearch = { search_text: '', gender: '', country_id: '', page: 1, limit: 10 };

function DonorList() {
  const navigate = useNavigate();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [donors, setDonors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [searchForm, setSearchForm] = useState(() => (urlParams ? {
    search_text: urlParams.get('search_text') || '',
    gender: urlParams.get('gender') || '',
    country_id: urlParams.get('country_id') || '',
    page: parseInt(urlParams.get('page'), 10) || 1,
    limit: parseInt(urlParams.get('limit'), 10) || 10,
  } : { ...defaultSearch }));

  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsDialogLoading, setDocsDialogLoading] = useState(false);
  const [docsDialogDonorName, setDocsDialogDonorName] = useState('');
  const [docsDialogDocs, setDocsDialogDocs] = useState([]);
  const [docsDialogDonorId, setDocsDialogDonorId] = useState(null);
  const [docsDialogTab, setDocsDialogTab] = useState(0);

  // Upload state
  const [uploadDocKey, setUploadDocKey] = useState('aadhaar');
  const [uploadDocNumber, setUploadDocNumber] = useState('');
  const [uploadDocFront, setUploadDocFront] = useState(null);
  const [uploadDocFrontPreview, setUploadDocFrontPreview] = useState(null);
  const [uploadDocBack, setUploadDocBack] = useState(null);
  const [uploadDocBackPreview, setUploadDocBackPreview] = useState(null);
  const [uploadDocIssuedBy, setUploadDocIssuedBy] = useState('');
  const [uploadDocValidTill, setUploadDocValidTill] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.master.getActiveCountries().then((res) => {
      if (res?.data?.countries?.length) {
        setCountries(res.data.countries);
        const def = res.data.countries.find((c) => c.is_default === 'Y' || c.is_default === true);
        // Don't clobber a country_id already restored from the URL.
        if (def && !urlParams?.get('country_id')) setSearchForm((p) => ({ ...p, country_id: def.id }));
      }
    });
    if (urlParams?.get('country_id')) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (overrides = {}) => {
    const params = { ...searchForm, ...overrides };
    if (!params.country_id) { setError('Country is required'); return; }
    setLoading(true);
    setError('');
    try {
      const query = {};
      if (params.search_text?.trim()) query.search_text = params.search_text.trim();
      if (params.gender) query.gender = params.gender;
      if (params.country_id) query.country_id = params.country_id;
      query.page = params.page || 1;
      query.limit = params.limit || 10;

      writeParams(query);
      const res = await api.contacts.searchDonors(query);
      if (res?.data) {
        setDonors(res.data.donors || []);
        const total = res.data.total || 0;
        const page = query.page || 1;
        const limit = query.limit || 10;
        setPagination({
          total,
          page,
          limit,
          pages: Math.ceil(total / limit) || 1,
        });
        setSearchPerformed(true);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to search donors');
      setDonors([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_, newPage) => {
    const updated = { ...searchForm, page: newPage };
    setSearchForm(updated);
    handleSearch({ page: newPage });
  };

  const handleReset = () => {
    setSearchForm({ ...defaultSearch });
    setDonors([]);
    setSearchPerformed(false);
    setError('');
    writeParams({});
  };

  const handleMenuOpen = (e, donor) => {
    setAnchorEl(e.currentTarget);
    setSelectedDonor(donor);
  };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedDonor(null); };

  const handleDocsOpen = async (donor) => {
    handleMenuClose();
    setDocsDialogOpen(true);
    setDocsDialogLoading(true);
    setDocsDialogTab(0);
    setDocsDialogDonorName([donor.first_name, donor.middle_name, donor.last_name].filter(Boolean).join(' '));
    setDocsDialogDonorId(donor.id);
    setDocsDialogDocs([]);
    // reset upload state
    setUploadDocKey('aadhaar');
    setUploadDocNumber('');
    setUploadDocFront(null);
    setUploadDocFrontPreview(null);
    setUploadDocBack(null);
    setUploadDocBackPreview(null);
    setUploadDocIssuedBy('');
    setUploadDocValidTill('');
    try {
      const res = await api.contacts.getDonorDetails(donor.external_code);
      const fullDonor = res?.data?.donor || res?.data;
      setDocsDialogDocs(fullDonor?.documents || []);
      if (!docsDialogDonorId) setDocsDialogDonorId(fullDonor?.id || donor.id);
    } catch {
      setDocsDialogDocs([]);
    } finally {
      setDocsDialogLoading(false);
    }
  };

  const handleUploadDocFileChange = (side, file) => {
    if (side === 'front') {
      setUploadDocFront(file);
      setUploadDocFrontPreview(file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    } else {
      setUploadDocBack(file);
      setUploadDocBackPreview(file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    }
  };

  const handleUploadDoc = async () => {
    const doc = DOC_TYPES.find((d) => d.key === uploadDocKey);
    if (!uploadDocNumber.trim()) { Swal.fire({ icon: 'warning', title: 'Enter document number' }); return; }
    if (!uploadDocFront) { Swal.fire({ icon: 'warning', title: 'Select front image/file' }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('contact_id', docsDialogDonorId);
      fd.append('document_number', uploadDocNumber.trim().toUpperCase());
      fd.append('type', uploadDocKey === 'voter' ? 'epic' : uploadDocKey);
      fd.append('file1', uploadDocFront);
      if (uploadDocBack) fd.append('file2', uploadDocBack);
      fd.append('is_default', 'Y');
      fd.append('file_sub_type_1', `front_${uploadDocKey}`);
      if (uploadDocBack) fd.append('file_sub_type_2', `back_${uploadDocKey}`);
      if (doc?.showIssuedBy && uploadDocIssuedBy) fd.append('issued_by', uploadDocIssuedBy);
      if (doc?.showValidTill && uploadDocValidTill) fd.append('valid_till', uploadDocValidTill);
      await api.contacts.uploadDonorDocuments(fd);
      Swal.fire({ icon: 'success', title: `${doc?.label || 'Document'} uploaded!`, timer: 1500, showConfirmButton: false });
      // refresh docs list
      setDocsDialogLoading(true);
      setDocsDialogTab(0);
      setUploadDocKey('aadhaar');
      setUploadDocNumber('');
      setUploadDocFront(null);
      setUploadDocFrontPreview(null);
      setUploadDocBack(null);
      setUploadDocBackPreview(null);
      setUploadDocIssuedBy('');
      setUploadDocValidTill('');
      const selected = donors.find((d) => d.id === docsDialogDonorId);
      if (selected) {
        const res = await api.contacts.getDonorDetails(selected.external_code);
        const fullDonor = res?.data?.donor || res?.data;
        setDocsDialogDocs(fullDonor?.documents || []);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Upload failed', text: err?.response?.data?.message || 'Failed to upload' });
    } finally {
      setUploading(false);
      setDocsDialogLoading(false);
    }
  };

  const getInitials = (first, last) => `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  const genderColor = (g) => (g === 'M' ? '#1976d2' : '#e91e8c');

  const formatDob = (dob) => {
    if (!dob) return 'N/A';
    const d = new Date(dob);
    return d.toLocaleDateString('en-GB');
  };

  const getTableData = () => {
    const columns = [
      { Header: 'Profile', accessor: 'profile', align: 'center' },
      { Header: 'Donor Code', accessor: 'donor_code', align: 'left' },
      { Header: 'Name', accessor: 'name', align: 'left' },
      { Header: 'Gender', accessor: 'gender', align: 'center' },
      { Header: 'Age / DOB', accessor: 'age_dob', align: 'center' },
      { Header: 'Phone', accessor: 'phone', align: 'left' },
      { Header: 'Nationality', accessor: 'nationality', align: 'left' },
      { Header: 'Tagged Procedure', accessor: 'tagged_procedure', align: 'left' },
      { Header: 'Actions', accessor: 'actions', align: 'center' },
    ];
    const rows = donors.map((donor) => ({
      profile: (
        <Box display="flex" justifyContent="center">
          <Avatar
            src={
              donor.profile_picture_base_url && donor.profile_picture_path
                ? donor.profile_picture_base_url + donor.profile_picture_path
                : ''
            }
            alt={donor.first_name}
            sx={{ width: 44, height: 44, bgcolor: genderColor(donor.gender), fontSize: 14, fontWeight: 'bold', border: '2px solid #e0e0e0' }}
          >
            {getInitials(donor.first_name, donor.last_name)}
          </Avatar>
        </Box>
      ),
      donor_code: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {donor.external_code || 'N/A'}
        </MDTypography>
      ),
      name: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {[donor.first_name, donor.middle_name, donor.last_name].filter(Boolean).join(' ')}
        </MDTypography>
      ),
      gender: (
        <Chip
          label={donor.gender === 'M' ? 'Male' : 'Female'}
          size="small"
          color={donor.gender === 'M' ? 'info' : 'secondary'}
        />
      ),
      age_dob: (
        <Box>
          <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
            {donor.date_of_birth ? `${calculateAge(donor.date_of_birth)} yrs` : 'N/A'}
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="regular">
            {formatDob(donor.date_of_birth)}
          </MDTypography>
        </Box>
      ),
      phone: (
        <MDTypography variant="caption" color="text" fontWeight="regular">
          {donor.phones?.[0]
            ? `+${donor.phones[0].country_code} ${donor.phones[0].phone_number}`
            : 'N/A'}
        </MDTypography>
      ),
      nationality: (
        <MDTypography variant="caption" color="text" fontWeight="regular">
          {donor.nationality || 'N/A'}
        </MDTypography>
      ),
      tagged_procedure: donor.tagged_procedure ? (
        <Box>
          <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
            {[donor.tagged_procedure.procedure_type, donor.tagged_procedure.procedure_sub_type].filter(Boolean).join(' / ')}
            {donor.tagged_procedure.procedure_code ? ` (${donor.tagged_procedure.procedure_code})` : ''}
          </MDTypography>
          {donor.tagged_procedure.patient_name && (
            <MDTypography variant="caption" color="text" fontWeight="regular" display="block">
              Patient: {donor.tagged_procedure.patient_name}
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text" fontWeight="regular">
            {formatDob(donor.tagged_procedure.attached_at)}
          </MDTypography>
        </Box>
      ) : (
        <Chip label="Not Tagged" size="small" variant="outlined" sx={{ color: '#888', borderColor: '#ccc' }} />
      ),
      actions: (
        <Box display="flex" justifyContent="center">
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, donor)}
            sx={{ color: logoColor, '&:hover': { backgroundColor: 'rgba(0,121,142,0.06)' } }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
      customStyles: donor.tagged_procedure ? { backgroundColor: '#fff9c4' } : {}, // For applying to the entire row
    }));
    return { columns, rows };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox pt={4} pb={4} px={2}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDTypography variant="h4" fontWeight="bold">Donor List</MDTypography>
          {Number(searchForm.country_id) === INDIA_COUNTRY_ID && (
            <button
              type="button"
              onClick={() => navigate('/donor-registration')}
              style={{
                padding: '10px 24px',
                background: '#00798E',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'background 0.3s ease',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
            >
              <PersonAddIcon style={{ fontSize: 18 }} />
              Register Donor
            </button>
          )}
        </Box>

        {/* Search */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Search (Name / Code)" value={searchForm.search_text}
                onChange={(e) => setSearchForm((p) => ({ ...p, search_text: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select value={searchForm.gender} label="Gender"
                  onChange={(e) => setSearchForm((p) => ({ ...p, gender: e.target.value }))} sx={{ height: 45 }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Country *</InputLabel>
                <Select value={searchForm.country_id} label="Country *"
                  onChange={(e) => setSearchForm((p) => ({ ...p, country_id: e.target.value }))} sx={{ height: 45 }}>
                  {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.country_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box display="flex" gap={1}>
                <button
                  type="button"
                  onClick={() => handleSearch({ page: 1 })}
                  style={{
                    padding: '10px 16px',
                    background: '#00798E',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: 1,
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'background 0.3s ease',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
                  onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
                >
                  <SearchIcon style={{ fontSize: 18 }} />
                  Search
                </button>
                <Tooltip title="Reset">
                  <IconButton onClick={handleReset}><ClearIcon /></IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          {error && <Typography color="error" variant="body2" mt={1}>{error}</Typography>}
        </Card>

        {/* Results */}
        {searchPerformed && (
          <Card>
            {donors.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Typography variant="h6" color="text.secondary">No donors found</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>Try adjusting your search criteria.</Typography>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pt={3} pb={1}>
                  <Typography variant="h6">
                    Search Results ({pagination.total} donors found)
                  </Typography>
                  <Chip label={`Page ${pagination.page} of ${pagination.pages}`} color="primary" variant="outlined" />
                </Box>
                <MDBox pt={1}>
                  <DataTable
                    table={getTableData()}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    customRowStyles={(row) => row.original.customStyles || {}}
                  />
                </MDBox>
                {pagination.pages > 1 && (
                  <Box display="flex" justifyContent="center" pb={3}>
                    <Pagination count={pagination.pages} page={pagination.page} onChange={handlePageChange} color="primary" />
                  </Box>
                )}
              </>
            )}
          </Card>
        )}

        {/* Context Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { navigate(`/donor-details/${selectedDonor?.external_code}`); handleMenuClose(); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { navigate(`/donor-edit/${selectedDonor?.external_code}`); handleMenuClose(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Donor</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDocsOpen(selectedDonor)}>
            <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Documents</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { navigate(`/donor-investigations/${selectedDonor?.external_code}`); handleMenuClose(); }}>
            <ListItemIcon><ScienceIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Investigations</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { navigate(`/donor-contact-histories/${selectedDonor?.external_code}`); handleMenuClose(); }}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Contact Histories</ListItemText>
          </MenuItem>
        </Menu>

        {/* Documents Dialog */}
        <Dialog
          open={docsDialogOpen}
          onClose={() => setDocsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 0 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <ArticleIcon sx={{ color: '#00798E', fontSize: 28 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>Documents</Typography>
                <Typography variant="body2" color="text.secondary">{docsDialogDonorName}</Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Box px={3} pt={1}>
            <Tabs
              value={docsDialogTab}
              onChange={(_, v) => setDocsDialogTab(v)}
              sx={{ borderBottom: '1px solid #e0e0e0' }}
              TabIndicatorProps={{ style: { backgroundColor: '#00798E' } }}
            >
              <Tab label="Uploaded Documents" sx={{ '&.Mui-selected': { color: '#00798E' } }} />
              <Tab label="Upload New Document" sx={{ '&.Mui-selected': { color: '#00798E' } }} />
            </Tabs>
          </Box>
          <Divider />
          <DialogContent sx={{ pt: 2, pb: 1 }}>

            {/* Tab 0 — View existing docs */}
            {docsDialogTab === 0 && (
              <>
                {docsDialogLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress sx={{ color: '#00798E' }} />
                  </Box>
                ) : docsDialogDocs.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <ArticleIcon sx={{ fontSize: 56, color: '#bdbdbd', mb: 1.5 }} />
                    <Typography variant="body1" color="text.secondary" fontWeight="medium">No documents uploaded yet</Typography>
                    <Typography variant="body2" color="text.disabled" mt={0.5}>
                      Use the &quot;Upload New Document&quot; tab to add documents.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Number</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Issued By</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Valid Till</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Files</TableCell>
                        </TableRow>
                        {docsDialogDocs.map((doc) => (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <DescriptionIcon sx={{ color: '#00798E', fontSize: 17 }} />
                                <Typography variant="body2" fontWeight="medium">
                                  {doc.documentType?.type_name || '—'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" letterSpacing={0.5}>
                                {doc.document_number || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {doc.issued_by || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {doc.valid_till ? new Date(doc.valid_till).toLocaleDateString('en-GB') : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {doc.is_verified === 'Y' ? (
                                <Box display="flex" alignItems="center" gap={0.5} color="green">
                                  <span>✓</span>
                                  <Typography variant="caption" fontWeight="medium">Verified</Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="warning.main" fontWeight="medium">Pending</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {doc.files?.length > 0 ? (
                                  doc.files.map((file) => {
                                    const fileUrl = `${file.base_url}${file.file_path}`;
                                    const rawLabel = file.sub_type ? file.sub_type.replace(/_/g, ' ').toUpperCase() : 'File';
                                    const label = file.sub_type
                                      ? file.sub_type.split('_')[0].charAt(0).toUpperCase() + file.sub_type.split('_')[0].slice(1)
                                      : 'View';
                                    return (
                                      <Tooltip key={file.id} title={rawLabel} arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => window.open(fileUrl, '_blank')}
                                          sx={{
                                            color: '#00798E',
                                            border: '1px solid #00798E',
                                            borderRadius: '6px',
                                            padding: '4px 8px',
                                            fontSize: 11,
                                            gap: 0.5,
                                            '&:hover': { backgroundColor: 'rgba(0,121,142,0.08)', transform: 'scale(1.05)' },
                                            transition: 'all 0.2s',
                                          }}
                                        >
                                          <VisibilityIcon sx={{ fontSize: 14 }} />
                                          <span style={{ fontWeight: 600 }}>{label}</span>
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  })
                                ) : (
                                  <Typography variant="caption" color="text.disabled">No files</Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* Tab 1 — Upload new document */}
            {docsDialogTab === 1 && (() => {
              const currentDoc = DOC_TYPES.find((d) => d.key === uploadDocKey);
              return (
                <Box>
                  <Grid container spacing={2} mt={0.5}>
                    {/* Document Type */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Document Type *</InputLabel>
                        <Select
                          value={uploadDocKey}
                          label="Document Type *"
                          onChange={(e) => {
                            setUploadDocKey(e.target.value);
                            setUploadDocIssuedBy('');
                            setUploadDocValidTill('');
                          }}
                          sx={{ height: 45 }}
                        >
                          {DOC_TYPES.map((d) => (
                            <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Document Number */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Document Number *"
                        value={uploadDocNumber}
                        onChange={(e) => setUploadDocNumber(e.target.value.toUpperCase())}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Grid>

                    {/* Issued By (conditional) */}
                    {currentDoc?.showIssuedBy && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Issued By"
                          value={uploadDocIssuedBy}
                          onChange={(e) => setUploadDocIssuedBy(e.target.value)}
                        />
                      </Grid>
                    )}

                    {/* Valid Till (conditional) */}
                    {currentDoc?.showValidTill && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Valid Till"
                          type="date"
                          value={uploadDocValidTill}
                          onChange={(e) => setUploadDocValidTill(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}

                    {/* Front File */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={1}>
                        Front Side *
                      </Typography>
                      <input
                        id="upload-doc-front"
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => handleUploadDocFileChange('front', e.target.files[0])}
                      />
                      <Box
                        component="label"
                        htmlFor="upload-doc-front"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed',
                          borderColor: uploadDocFront ? '#00798E' : '#bdbdbd',
                          borderRadius: 2,
                          p: 2,
                          minHeight: 110,
                          cursor: 'pointer',
                          backgroundColor: uploadDocFront ? 'rgba(0,121,142,0.04)' : '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' },
                        }}
                      >
                        {uploadDocFrontPreview ? (
                          <Box textAlign="center">
                            <img src={uploadDocFrontPreview} alt="front" style={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            <Typography variant="caption" display="block" color="text.secondary" noWrap mt={0.5}>{uploadDocFront?.name}</Typography>
                          </Box>
                        ) : uploadDocFront ? (
                          <Box textAlign="center">
                            <DescriptionIcon sx={{ fontSize: 38, color: '#00798E' }} />
                            <Typography variant="caption" display="block" noWrap>{uploadDocFront?.name}</Typography>
                          </Box>
                        ) : (
                          <Box textAlign="center">
                            <CloudUploadIcon sx={{ fontSize: 36, color: '#bdbdbd' }} />
                            <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">Click to upload front</Typography>
                            <Typography variant="caption" color="text.disabled">Image or PDF</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Back File */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={1}>
                        Back Side (optional)
                      </Typography>
                      <input
                        id="upload-doc-back"
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => handleUploadDocFileChange('back', e.target.files[0])}
                      />
                      <Box
                        component="label"
                        htmlFor="upload-doc-back"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed',
                          borderColor: uploadDocBack ? '#00798E' : '#bdbdbd',
                          borderRadius: 2,
                          p: 2,
                          minHeight: 110,
                          cursor: 'pointer',
                          backgroundColor: uploadDocBack ? 'rgba(0,121,142,0.04)' : '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' },
                        }}
                      >
                        {uploadDocBackPreview ? (
                          <Box textAlign="center">
                            <img src={uploadDocBackPreview} alt="back" style={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            <Typography variant="caption" display="block" color="text.secondary" noWrap mt={0.5}>{uploadDocBack?.name}</Typography>
                          </Box>
                        ) : uploadDocBack ? (
                          <Box textAlign="center">
                            <DescriptionIcon sx={{ fontSize: 38, color: '#00798E' }} />
                            <Typography variant="caption" display="block" noWrap>{uploadDocBack?.name}</Typography>
                          </Box>
                        ) : (
                          <Box textAlign="center">
                            <CloudUploadIcon sx={{ fontSize: 36, color: '#bdbdbd' }} />
                            <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">Click to upload back</Typography>
                            <Typography variant="caption" color="text.disabled">Image or PDF</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Submit button */}
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" mt={1}>
                        <button
                          type="button"
                          disabled={uploading}
                          onClick={handleUploadDoc}
                          style={{
                            padding: '10px 28px',
                            background: uploading ? '#cccccc' : '#00798E',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'background 0.3s ease',
                          }}
                          onMouseOver={(e) => { if (!uploading) e.currentTarget.style.background = '#006379'; }}
                          onMouseOut={(e) => { if (!uploading) e.currentTarget.style.background = '#00798E'; }}
                        >
                          {uploading
                            ? <><CircularProgress size={16} style={{ color: '#fff' }} /> Uploading...</>
                            : <><SaveIcon style={{ fontSize: 18 }} /> Upload Document</>
                          }
                        </button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <button
              type="button"
              onClick={() => setDocsDialogOpen(false)}
              style={{
                padding: '8px 24px',
                background: '#e0e0e0',
                color: '#333',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'background 0.3s ease',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#bdbdbd')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#e0e0e0')}
            >
              Close
            </button>
          </DialogActions>
        </Dialog>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorList;
